import { clsx } from "clsx";
import Link from "next/link";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";

/**
 * Variant rule: `cta` = the conversion action (orange — Start Quiz,
 * Enquire); `primary` = default affirmative; `secondary` = alternative action;
 * `link` = inline/low-emphasis. Sanctioned exceptions: the Hero button row
 * deliberately mixes cta + secondary + the green CampfireChatButton — see
 * Hero.tsx — and the cheaper_available empty state on the owner results page
 * mixes cta (BudgetFlexRecovery, Ember per the budget-flexibility design
 * handoff) + secondary Retry + CampfireChatButton — see
 * app/quiz/results/[questionnaireId]/page.tsx. Full reference: /design-system.
 */
export type ButtonVariant = "primary" | "secondary" | "link" | "cta";

/**
 * Size steps. `sm`/`md`/`lg` are the classic chrome sizes; the
 * `-base` steps pair the same chrome with 16px text, and `xl` is the 18px/56px
 * emphasis step (ResultsActionBar's Book Your Adventure CTA). Pick a step instead of
 * forcing text size with a `text-*!` class on the call site.
 */
type ButtonSize = "sm" | "sm-base" | "md" | "md-base" | "lg" | "xl";

type Common = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  className?: string;
  children?: ReactNode;
};

export type ButtonProps =
  | (Common &
      Omit<ButtonHTMLAttributes<HTMLButtonElement>, "size" | "className" | "children"> & {
        href?: never;
      })
  | (Common &
      Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "children"> & {
        href: string;
      });

function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={clsx(
        "inline-block size-4 animate-spin rounded-full border-2 border-current border-r-transparent",
        className,
      )}
    />
  );
}

const base =
  "shrink-0 items-center justify-center gap-2 whitespace-nowrap font-medium leading-none transition-colors " +
  "cursor-pointer disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-60 " +
  "focus-ring";

const variantClass: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-primary-foreground hover:bg-primary-hover shadow-[var(--shadow-sm)]",
  cta:
    "bg-cta text-white hover:bg-cta-hover shadow-[var(--shadow-sm)]",
  secondary:
    "bg-surface-muted text-text border border-border hover:border-border-strong hover:bg-surface shadow-[var(--shadow-sm)]",
  link: "bg-transparent text-primary hover:text-primary-hover underline underline-offset-4 decoration-[color-mix(in_oklab,var(--bsa-primary)_35%,transparent)]",
};

const sizeClass: Record<ButtonSize, string> = {
  sm: "h-9 px-4 text-sm rounded-full",
  "sm-base": "h-9 px-4 text-base rounded-full",
  md: "h-12 px-6 text-sm rounded-full",
  "md-base": "h-12 px-6 text-base rounded-full",
  lg: "h-14 px-9 text-base rounded-full",
  xl: "h-14 px-9 text-lg rounded-full",
};

export function Button(props: ButtonProps) {
  const {
    variant = "primary",
    size = "md",
    loading = false,
    fullWidth = false,
    className,
    children,
    ...rest
  } = props;

  const widthClass = fullWidth ? "flex w-full" : "inline-flex";

  const content = (
    <>
      {loading ? <Spinner /> : null}
      <span className={clsx("inline-flex items-center gap-3", loading && "opacity-90")}>
        {children}
      </span>
    </>
  );

  if ("href" in rest && typeof rest.href === "string") {
    const { href, ...linkProps } = rest;
    return (
      <Link
        href={href}
        className={clsx(base, widthClass, variantClass[variant], sizeClass[size], className)}
        aria-busy={loading || undefined}
        aria-disabled={loading || undefined}
        tabIndex={loading ? -1 : undefined}
        {...linkProps}
      >
        {content}
      </Link>
    );
  }

  const { disabled, type, ...buttonProps } = rest;
  const isDisabled = Boolean(disabled || loading);

  return (
    <button
      type={type ?? "button"}
      className={clsx(base, widthClass, variantClass[variant], sizeClass[size], className)}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...buttonProps}
    >
      {content}
    </button>
  );
}
